import { ChartSettingsT, PivotFieldsAggridT } from 'business/definitions';
import _ from 'lodash';
import { getPivotQuerySettings } from 'routes/BudgetModule/config/queries';
import { getChartSettings } from 'routes/BudgetModule/helpers/chartHelpers';
import { SelectedChartForArgumentT } from '../Analyse';

type AddProjectPivotGridToArgumentProps = {
    pivotTableTitle: string;
    projectPivotGridQuery: QueryT;
    onAddToArgumentModalOpen: (
        selectedChart: SelectedChartForArgumentT,
        indicatorName: string,
    ) => void;
    pivotFieldsAggridSettings: PivotFieldsAggridT | undefined;
    indicatorName: string;
};

/**
 * Helper for managing logic related to adding projects to an argument.
 * It provides a function `addToArgument` that can be used to open
 * the argument modal with specific settings.
 *
 * @param pivotTableTitle - The title of the pivot table.
 * @param projectPivotGridQuery - The query for the pivot grid.
 * @param onAddToArgumentModalOpen - Function to open the argument modal.
 * @param pivotFieldsAggridSettings - Settings for pivot fields in Ag-Grid.
 * @returns the `addToArgument` function.
 */
export const addProjectPivotGridToArgument = ({
    pivotTableTitle,
    projectPivotGridQuery,
    onAddToArgumentModalOpen,
    pivotFieldsAggridSettings,
    indicatorName,
}: AddProjectPivotGridToArgumentProps): (() => void) => {
    const pivotTableFieldsSettings = getPivotQuerySettings(
        projectPivotGridQuery,
        ['inscription.expense_income'],
        [],
    );
    const getPivotTableSettings = (): ChartSettingsT => {
        // don't remove this cloneDeep, else we modify global variables from getChartSettings
        const settings = _.cloneDeep(getChartSettings('pivot_table'));
        delete settings.legendPosition;
        settings.pivotFields = pivotTableFieldsSettings;
        settings.pivotFieldsAggrid = pivotFieldsAggridSettings;
        settings.showAggregates = false;
        // Hack to ensure correct table format, it avoids using Analyse format when it's from budget.
        settings.pivotTableExportedFromDecisionToBudget = false;
        return settings;
    };
    const pivotTableSettingsQuerySpecific = getPivotTableSettings();
    const addToArgument = () =>
        onAddToArgumentModalOpen(
            {
                query: projectPivotGridQuery,
                title: pivotTableTitle,
                settings: pivotTableSettingsQuerySpecific,
                use_get_data: false,
            },
            indicatorName,
        );

    return addToArgument;
};
